import clsx from 'clsx';
import moment from 'moment';
import React, { ReactNode } from 'react';

import { DEFAULT_DATE_FORMAT } from '@/src/config';
import useSearchParamsState from '@/src/hooks/useSearchParamsState';
import { cn } from '@/src/util/ui';

import { mapEmailThreadEntry } from './EmailThreadEntry/EmailThreadEntry';
import { mapFileEntry } from './FileEntry/FileEntry';
import StatusIcons from './StatusIcons/StatusIcons';

import EntryCard from '../../../Elements/EntryCard/EntryCard';
import { useInbox } from '../../../store/inbox.store';
import { useInboxEntryPanel } from '../../../store/inboxEntryPanel.store';
import { InboxEntry, InboxEntryType } from '../../../types';
import useInboxSearchParams from '../../../useInboxSearchParams';
import { useSyncStaleEntry } from '../../EntryPanel/useSyncStaleEntry';

export const DATE_FORMAT = `${DEFAULT_DATE_FORMAT} h:mm A`;

export const mapSidebarInboxEntryCommonProps = (rawInboxEntry) => {
  return {
    id: rawInboxEntry['source-id'],
    source: rawInboxEntry.source,
    subject: rawInboxEntry.subject,
    isRead: rawInboxEntry.read,
    isDeleted: rawInboxEntry.trash,
    from: rawInboxEntry.from,
    createdDate: rawInboxEntry.date,
    isAccountRead: rawInboxEntry['account-read'],
  };
};

export const mapInboxEntry = (rawInboxEntry): InboxEntry => {
  return rawInboxEntry.source === InboxEntryType.THREAD
    ? {
        data: mapEmailThreadEntry(rawInboxEntry),
        type: InboxEntryType.THREAD,
      }
    : {
        data: mapFileEntry(rawInboxEntry),
        type: InboxEntryType.DOCUMENT,
      };
};

type InboxEntryProps = {
  inboxEntry: InboxEntry;
  date: string;
  header: ReactNode;
  children?: React.ReactNode;
  statusIcons?: null | React.ReactNode;
  containerClassName?: string;
  onClick: () => void;
};

const InboxEntry = ({
  date,
  inboxEntry,
  header,
  onClick,
  statusIcons = null,
  children = null,
  containerClassName = '',
}: InboxEntryProps) => {
  const { inboxEntry: selectedInboxEntry } = useInboxEntryPanel();
  const { isTrash } = useInboxSearchParams();
  const { clearSocket } = useInbox();
  const syncStaleEntry = useSyncStaleEntry();
  const showStatusIcons = !isTrash;
  const [, setPreselectedInboxEntryId] = useSearchParamsState();

  const isRead = inboxEntry.data.isRead;
  const isSelected = inboxEntry.data.id === selectedInboxEntry?.data.id;

  const backgroundColor = isSelected
    ? 'tw-bg-cfo-blue-200'
    : isRead
    ? 'tw-bg-cfo-grey-100'
    : !isRead
    ? 'tw-bg-white'
    : '';

  const handleClick = () => {
    setPreselectedInboxEntryId({ id: null });
    onClick();

    syncStaleEntry({ shouldSyncInboxEntry: false });
    clearSocket();
  };

  return (
    <EntryCard
      className={cn(
        'tw-pt-[15px] tw-pb-[15px] tw-pl-[15px] tw-pr-[15px]',
        'hover:tw-bg-cfo-blue-200',
        backgroundColor
      )}
      onClick={handleClick}
      isActive={isSelected}
    >
      {/* Recipient Name && Date-Time */}
      <div className={clsx('tw-flex tw-items-center tw-justify-between', 'tw-pb-[5px]')}>
        {header}
        <p className={clsx('tw-text-[12px] tw-text-cfo-black', 'tw-m-0')}>
          {moment(date).format(DATE_FORMAT)}
        </p>
      </div>

      <div className={clsx('tw-flex tw-items-start tw-justify-between')}>
        {/* Email Subject / File Name && Email Preview */}
        <div className={cn(containerClassName)}>{children}</div>

        {showStatusIcons && statusIcons && <StatusIcons>{statusIcons}</StatusIcons>}
      </div>
    </EntryCard>
  );
};

export default InboxEntry;
